import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dithering } from '@paper-design/shaders-react';

const CALENDLY = 'https://calendly.com/contactnetworkcl/15-min-meeting';

/* ─── ORBS (idénticos al Homepage) ─────────────────────────── */
const BackgroundOrbs = () => {
  const wrapRef = useRef(null);
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const orcs   = [...wrap.children];
    const speeds = [0.042, -0.032, 0.068, -0.051, 0.085, -0.063];
    let rafId = null;
    const onScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        const y = window.scrollY;
        orcs.forEach((el, i) => { el.style.transform = `translateY(${y * speeds[i]}px)`; });
        rafId = null;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { window.removeEventListener('scroll', onScroll); if (rafId) cancelAnimationFrame(rafId); };
  }, []);
  return (
    <div ref={wrapRef} className="agd-orbs-wrap" aria-hidden="true">
      <div className="agd-orc"><div className="agd-orb agd-orb-1"/></div>
      <div className="agd-orc"><div className="agd-orb agd-orb-2"/></div>
      <div className="agd-orc"><div className="agd-orb agd-orb-3"/></div>
      <div className="agd-orc"><div className="agd-orb agd-orb-4"/></div>
      <div className="agd-orc"><div className="agd-orb agd-orb-5"/></div>
      <div className="agd-orc"><div className="agd-orb agd-orb-6"/></div>
    </div>
  );
};

/* ─── PAGE ─────────────────────────────────────────────────── */
const AgendaDemo = () => {
  const navigate = useNavigate();
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    setReducedMotion(window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }, []);

  return (
    <div className="agd-root">
      <BackgroundOrbs />

      {/* Dither — igual al hero del Homepage */}
      <div className="agd-dither" aria-hidden="true">
        <Dithering
          style={{ width: '100%', height: '100%' }}
          colorBack="#00000000"
          colorFront="#0071e3"
          shape="swirl"
          type="4x4"
          size={2}
          speed={reducedMotion ? 0 : 0.35}
          scale={1.1}
        />
      </div>

      {/* Volver — flotante, sin borde */}
      <button className="agd-back" onClick={() => navigate('/')}>
        ← Volver
      </button>

      {/* Contenido centrado */}
      <main className="agd-main">

        {/* Frase de valor — cursiva, Inter 300 */}
        <h1 className="agd-headline">
          ¿Listo para llevar<br/>Contact a tu equipo?
        </h1>

        {/* Email + Hablemos */}
        <div className="agd-contact-row">
          <div className="agd-col">
            <span className="agd-col-label">Email</span>
            <a
              href="mailto:ventas@contactnetwork.cl"
              className="agd-col-value agd-email"
            >
              ventas@contactnetwork.cl
            </a>
          </div>

          <div className="agd-divider" aria-hidden="true"/>

          <div className="agd-col">
            <span className="agd-col-label">Hablemos</span>
            <button
              className="agd-btn-cta"
              onClick={() => window.open(CALENDLY, '_blank', 'noopener,noreferrer')}
            >
              Agendar reunión
            </button>
          </div>
        </div>

      </main>

      <style>{CSS}</style>
    </div>
  );
};

/* ─── CSS ────────────────────────────────────────────────────── */
const CSS = `

/* ── ROOT ── */
.agd-root {
  font-family: 'Inter', system-ui, sans-serif;
  background: #F9F9F8;
  min-height: 100vh;
  overflow-x: hidden;
  position: relative;
}

/* ── ORBS ── */
.agd-orbs-wrap { position: fixed; inset: 0; pointer-events: none; z-index: 0; overflow: hidden; }
.agd-orc       { position: absolute; will-change: transform; }
.agd-orb       { position: absolute; border-radius: 50%; top: 0; left: 0; }

.agd-orc:nth-child(1) { width: 900px; height: 900px; top: -250px; left: -300px; }
.agd-orb-1 { width:100%;height:100%;background:radial-gradient(circle at 50% 50%,rgba(59,130,246,.48) 0%,rgba(59,130,246,.22) 38%,transparent 66%);animation:agdDrift1 20s ease-in-out infinite; }

.agd-orc:nth-child(2) { width: 720px; height: 720px; top: 5%; right: -240px; }
.agd-orb-2 { width:100%;height:100%;background:radial-gradient(circle at 50% 50%,rgba(99,102,241,.40) 0%,rgba(99,102,241,.18) 40%,transparent 66%);animation:agdDrift2 24s ease-in-out infinite; }

.agd-orc:nth-child(3) { width: 650px; height: 650px; top: 38%; left: -180px; }
.agd-orb-3 { width:100%;height:100%;background:radial-gradient(circle at 50% 50%,rgba(37,99,235,.44) 0%,rgba(37,99,235,.18) 40%,transparent 66%);animation:agdDrift3 18s ease-in-out infinite; }

.agd-orc:nth-child(4) { width: 680px; height: 680px; top: 45%; right: -200px; }
.agd-orb-4 { width:100%;height:100%;background:radial-gradient(circle at 50% 50%,rgba(29,78,216,.38) 0%,rgba(29,78,216,.16) 40%,transparent 66%);animation:agdDrift1 26s ease-in-out infinite reverse; }

.agd-orc:nth-child(5) { width: 760px; height: 760px; top: 72%; left: -200px; }
.agd-orb-5 { width:100%;height:100%;background:radial-gradient(circle at 50% 50%,rgba(96,165,250,.46) 0%,rgba(96,165,250,.20) 40%,transparent 66%);animation:agdDrift2 22s ease-in-out infinite 3s; }

.agd-orc:nth-child(6) { width: 520px; height: 520px; top: 80%; right: -140px; }
.agd-orb-6 { width:100%;height:100%;background:radial-gradient(circle at 50% 50%,rgba(147,197,253,.40) 0%,rgba(147,197,253,.16) 40%,transparent 66%);animation:agdDrift3 28s ease-in-out infinite 6s; }

@keyframes agdDrift1 {
  0%,100% { transform: translate(0,0) scale(1); }
  33%     { transform: translate(32px,-55px) scale(1.06); }
  66%     { transform: translate(-22px,35px) scale(0.96); }
}
@keyframes agdDrift2 {
  0%,100% { transform: translate(0,0) scale(1); }
  40%     { transform: translate(-40px,28px) scale(1.05); }
  72%     { transform: translate(24px,-44px) scale(0.95); }
}
@keyframes agdDrift3 {
  0%,100% { transform: translate(0,0) scale(1); }
  50%     { transform: translate(28px,48px) scale(1.04); }
}

/* ── DITHER ── */
.agd-dither {
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  opacity: 0.18;
  mask-image: linear-gradient(180deg, #000 0%, #000 55%, transparent 92%);
  -webkit-mask-image: linear-gradient(180deg, #000 0%, #000 55%, transparent 92%);
  overflow: hidden;
}

/* ── BACK BUTTON ── */
.agd-back {
  position: fixed;
  top: 28px;
  left: 36px;
  z-index: 10;
  background: none;
  border: none;
  font-family: 'Inter', system-ui, sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: rgba(17,17,17,0.38);
  cursor: pointer;
  padding: 6px 0;
  transition: color .15s;
  letter-spacing: -.01em;
}
.agd-back:hover { color: rgba(17,17,17,0.75); }

/* ── MAIN ── */
.agd-main {
  position: relative;
  z-index: 1;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 80px 48px;
  box-sizing: border-box;
}

/* ── HEADLINE ── */
.agd-headline {
  font-family: 'Inter', system-ui, sans-serif;
  font-weight: 300;
  font-style: italic;
  font-size: clamp(44px, 6.5vw, 88px);
  line-height: 1.06;
  letter-spacing: -.025em;
  color: #111111;
  margin: 0 0 72px;
  max-width: 820px;
}

/* ── CONTACT ROW ── */
.agd-contact-row {
  display: flex;
  align-items: center;
  gap: 56px;
}

.agd-col {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
}

.agd-col-label {
  font-family: 'Inter', system-ui, sans-serif;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: .12em;
  text-transform: uppercase;
  color: rgba(17,17,17,0.35);
}

.agd-col-value {
  font-family: 'Inter', system-ui, sans-serif;
  font-size: 15px;
  font-weight: 400;
  color: rgba(17,17,17,0.72);
  letter-spacing: -.01em;
}

.agd-email {
  text-decoration: none;
  transition: color .15s;
}
.agd-email:hover { color: #111111; }

.agd-divider {
  width: 1px;
  height: 52px;
  background: rgba(17,17,17,0.10);
  flex-shrink: 0;
}

/* ── CTA BUTTON ── */
.agd-btn-cta {
  font-family: 'Inter', system-ui, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #ffffff;
  background: #111111;
  border: none;
  border-radius: 100px;
  padding: 11px 24px;
  cursor: pointer;
  letter-spacing: -.01em;
  transition: opacity .15s, transform .2s cubic-bezier(.22,1,.36,1);
}
.agd-btn-cta:hover {
  opacity: .84;
  transform: translateY(-2px);
}

/* ── RESPONSIVE ── */
@media (max-width: 640px) {
  .agd-back { left: 20px; top: 20px; }
  .agd-main { padding: 70px 24px 60px; }
  .agd-contact-row { flex-direction: column; gap: 32px; align-items: center; }
  .agd-col { align-items: center; }
  .agd-divider { width: 52px; height: 1px; }
}

@media (prefers-reduced-motion: reduce) {
  .agd-orc, .agd-orb { will-change: auto; animation: none; }
}
`;

export default AgendaDemo;
